'use client'

import { html } from '@ensemble.music/ui'
import { ArrowLeft, ArrowRight, Camera, type LucideIcon, Pause, Play } from 'lucide-react'
import { useReducedMotion } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { eventTypes } from './content'
import { Parallax } from './Parallax'
import { SectionHeading } from './SectionHeading'

const AUTOPLAY_MS = 4500

/**
 * The occasions as a carousel that keeps running on its own.
 *
 * The track is a native scroll container with CSS scroll snapping, so a swipe, a trackpad and the
 * arrow buttons all move the same thing and the whole list stays reachable without JS. Everything
 * the WAI-ARIA carousel pattern asks for is here: a labelled region with
 * aria-roledescription="carousel", previous/next buttons, a position in every card's label and -
 * because the rotation starts by itself - a pause control. Rotation also stops while a pointer is
 * over the track or the focus is inside it, and never starts under prefers-reduced-motion.
 *
 * Each card is one button that opens the request modal with the occasion preselected: no control
 * nested inside another, and a tap does the same as a click. The slides carry no role="group" of
 * their own - the card is a single labelled control whose label already names its position, so the
 * extra grouping would only add noise.
 */
export function EventTypes({ onPick }: { onPick: (occasion: string) => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const [playing, setPlaying] = useState(true)
  const [held, setHeld] = useState(false)

  const go = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current
      const slide = track?.firstElementChild
      if (!track || !slide) return

      const behavior = reduced ? 'auto' : 'smooth'
      // The gap is part of the step, so a slide always lands flush with the left edge.
      const step = slide.getBoundingClientRect().width + 16
      const end = track.scrollWidth - track.clientWidth
      // Wrap instead of stopping - "durchlaufen" is the point of the thing.
      if (direction === 1 && track.scrollLeft >= end - 4) track.scrollTo({ left: 0, behavior })
      else if (direction === -1 && track.scrollLeft <= 4) track.scrollTo({ left: end, behavior })
      else track.scrollBy({ left: step * direction, behavior })
    },
    [reduced]
  )

  useEffect(() => {
    if (reduced || !playing || held) return
    const timer = setInterval(() => go(1), AUTOPLAY_MS)
    return () => clearInterval(timer)
  }, [reduced, playing, held, go])

  return (
    <html.section
      id="anlaesse"
      className="flex flex-col gap-12"
    >
      <html.div className="flex flex-row flex-wrap items-end justify-between gap-6">
        {/* The one kicker left on the page, and it is a plain sentence rather than an all-caps
            label - it names the nav anchor the link above jumps to. */}
        <SectionHeading
          kicker="Anlässe"
          title="Für welchen Anlass suchst du Musik?"
          lead="Wähle aus, worum es geht – wir fragen gezielt die Bands an, die dazu passen und an deinem Datum Zeit haben."
        />
        <html.div className="flex flex-row items-center gap-2">
          <CarouselButton
            label="Vorheriger Anlass"
            icon={ArrowLeft}
            onClick={() => go(-1)}
          />
          <CarouselButton
            label={playing ? 'Automatisch weiterblättern pausieren' : 'Automatisch weiterblättern'}
            icon={playing ? Pause : Play}
            onClick={() => setPlaying((value) => !value)}
          />
          <CarouselButton
            label="Nächster Anlass"
            icon={ArrowRight}
            onClick={() => go(1)}
          />
        </html.div>
      </html.div>

      {/* custom classes stay on plain DOM elements: mixing one into an html.* className makes
          Tamagui fall back to inline styles, which then differ between server and client */}
      {/* a real <section> rather than role="region": same semantics, no role attribute to
          argue with. */}
      <section
        aria-roledescription="carousel"
        aria-label="Anlässe"
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)}
        onBlur={() => setHeld(false)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') go(1)
          else if (event.key === 'ArrowLeft') go(-1)
          else return
          event.preventDefault()
        }}
      >
        <div
          ref={trackRef}
          className="landing-carousel"
        >
          {eventTypes.map((event, index) => (
            <div
              key={event.id}
              className="landing-slide"
            >
              <html.button
                type="button"
                onClick={() => onPick(event.occasion)}
                aria-label={`Anfrage für ${event.title} starten – ${index + 1} von ${eventTypes.length}`}
                className="flex flex-col w-full overflow-hidden text-left bg-surface border border-border-color rounded-card hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring"
              >
                <EventMedia
                  image={event.image}
                  title={event.title}
                />
                <html.div className="flex flex-col gap-2 grow p-5 max-sm:p-4">
                  <html.span className="text-2xl font-semibold text-color">{event.title}</html.span>
                  <html.span className="grow text-base text-color-secondary">
                    {event.blurb}
                  </html.span>
                  <html.span className="text-base font-semibold text-primary-text">
                    Anfrage starten
                  </html.span>
                </html.div>
              </html.button>
            </div>
          ))}
        </div>
      </section>
    </html.section>
  )
}

/** Icon-only control, so the accessible name comes from aria-label and the 44 px target stays. */
function CarouselButton({
  label,
  icon: Glyph,
  onClick,
}: {
  label: string
  icon: LucideIcon
  onClick: () => void
}) {
  return (
    <html.button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex flex-row items-center justify-center w-11 h-11 rounded-full bg-surface border border-border-color-control text-color hover:bg-primary-soft hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus-ring"
    >
      <Glyph
        size={20}
        aria-hidden="true"
      />
    </html.button>
  )
}

/** A real photo once there is one, a labelled placeholder tile until then. */
function EventMedia({ image, title }: { image: string | null; title: string }) {
  return (
    <div className="landing-slide-media">
      {/* The drift is small on purpose: the tile is clipped by the card, so a big one would
          show its edge. */}
      <Parallax distance={14}>
        {image ? (
          <img
            src={image}
            alt=""
            loading="lazy"
            className="landing-slide-image"
          />
        ) : (
          <html.div
            aria-hidden="true"
            className="flex flex-col items-center justify-center gap-2 w-full h-[220px] bg-background-muted"
          >
            <html.span className="text-color-tertiary">
              <Camera
                size={28}
                aria-hidden="true"
              />
            </html.span>
            <html.span className="px-2 text-center text-sm text-color-tertiary">
              Foto {title} folgt
            </html.span>
          </html.div>
        )}
      </Parallax>
      {/* Unsplash license needs no credit, the owner wants one anyway. Text, not a link: the whole
          card is already a button, and a link inside a button is invalid. */}
      {image ? <span className="landing-photo-credit">@unsplash</span> : null}
    </div>
  )
}
